import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CoreService, DialogService, FormNavigationService, SpinnerService } from 'dspx-core';

import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { AppSelector, ComponentSelectors, NAV_TAB_NUMBERS, QUESTION_CODES, QUESTION_PATHS, REPORT_TYPES } from '../../constants/app-data.constants';

import { ReportListGridFilterRequest } from '../../interfaces/report-list-grid-filter-request.interface';

import { QuestionPath } from '../../helpers/config-data-parser';

import { HtmlHelperService } from '../../services/html-helper.service';
import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ResponseParserService } from '../../services/response-parser.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-report-list',
  templateUrl: './report-list.component.html',
  styleUrls: ['./report-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  activeDialog: string = '';

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private coreService: CoreService) {
    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.reportList,
      () => API_ENDPOINTS.dashboard.getReports,
      true
    );
  }

  ngOnInit(): void {
    this.ignoreCanDeactivateGuard = true;
    this.coreService.conditionFunctions['bpDataNotEmpty'] = () => this.bpDataNotEmpty();

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();
    this.waitForServiceWorker();
  }

  ngOnDestroy(): void {
    this.completeDestroy();
  }

  formLoad(event: any): void {
    this.configData = event.configData;
    this.loadGridDataAsPromise(
      this.getFilterRequest(QUESTION_CODES.reportList.businessProgressFilter),
      () => this.processGridTotals(NAV_TAB_NUMBERS.reportList.businessProgress),
      NAV_TAB_NUMBERS.reportList.businessProgress
    )
      .then(() => {
        return this.loadGridDataAsPromise(
          this.getFilterRequest(QUESTION_CODES.reportList.invoicingSummaryFilter),
          () => this.processGridTotals(NAV_TAB_NUMBERS.reportList.invoicingSummary),
          NAV_TAB_NUMBERS.reportList.invoicingSummary
        );
      })
      .catch((error) => {
        console.error('Error loading reports:', error);
      });
  }

  private async loadGridDataAsPromise<T>(
    filterRequest?: T,
    onGridLoaded?: () => void,
    currentNavTabNumber?: number
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      const wrappedCallback = () => {
        if (onGridLoaded) {
          onGridLoaded();
        }
        resolve();
      };

      this.loadGridData(filterRequest, wrappedCallback, currentNavTabNumber);
    });
  }
  
  filterData(event: any): void {
    const filterRequest = this.getFilterRequest(event.field.key);
    const navTabNumber = this.getCurrentNavTabNumber(event.field.key);

    this.loadGridData(filterRequest, () => this.processGridTotals(navTabNumber), navTabNumber);
  }

  private getCurrentNavTabNumber(reportToFilter: string): number {
    switch (reportToFilter) {
      case QUESTION_CODES.reportList.businessProgressFilter:
        return NAV_TAB_NUMBERS.reportList.businessProgress;

      case QUESTION_CODES.reportList.invoicingSummaryFilter:
        return NAV_TAB_NUMBERS.reportList.invoicingSummary;

      default:
        return null;
    }
  }

  private getFilterRequest(reportToFilter: string): ReportListGridFilterRequest {

    if (reportToFilter === QUESTION_CODES.reportList.businessProgressFilter) {
      const filterQuestionModel = this.getQuestionValue(this.model, ...QUESTION_PATHS.reportList.bpReportFilter);
      return {
        reportType: REPORT_TYPES.BusinessProgress,
        contractors: filterQuestionModel?.bpContractor,
        startDate: filterQuestionModel?.bpStartDate,
        endDate: filterQuestionModel?.bpEndDate
      }
    }
    else if (reportToFilter === QUESTION_CODES.reportList.invoicingSummaryFilter) {
      const filterQuestionModel = this.getQuestionValue(this.model, ...QUESTION_PATHS.reportList.isReportFilter);
      return {
        reportType: REPORT_TYPES.InvoicingSummary,
        contractors: [filterQuestionModel?.isContractor],
        startDate: filterQuestionModel?.isStartDate,
        endDate: filterQuestionModel?.isEndDate,
        formType: filterQuestionModel?.isFormType
      }
    }

    return null;
  }

  private bpDataNotEmpty(): boolean {
    var bpData = this.getQuestionValue(this.model, ...QUESTION_PATHS.reportList.bpReport);
    return bpData != undefined && bpData.length > 0;
  }

  // Process totals after grid data is loaded
  private processGridTotals(navTabNumber: number): void {
    if (navTabNumber === NAV_TAB_NUMBERS.reportList.businessProgress) {
      this.extractAndMoveTotalsToSeparateGrid(navTabNumber);
    }
    else if (navTabNumber === NAV_TAB_NUMBERS.reportList.invoicingSummary) {
      this.extractAndMoveTotalsToSeparateGrid(navTabNumber);
    }
  }

  private extractAndMoveTotalsToSeparateGrid(navTabNumber: number): void {

    const getMainGridQuestionPath = (navTabNumber: number) => {
      switch (navTabNumber) {
        case NAV_TAB_NUMBERS.reportList.businessProgress: return QUESTION_PATHS.reportList.bpReport;
        case NAV_TAB_NUMBERS.reportList.invoicingSummary: return QUESTION_PATHS.reportList.isReport;
      }
    }
    const mainGridQuestionPath = <QuestionPath>getMainGridQuestionPath(navTabNumber);

    const getTotalsGridQuestionPath = (navTabNumber: number) => {
      switch (navTabNumber) {
        case NAV_TAB_NUMBERS.reportList.businessProgress: return QUESTION_PATHS.reportList.bpReportTotals;
        case NAV_TAB_NUMBERS.reportList.invoicingSummary: return QUESTION_PATHS.reportList.isReportTotals;
      }
    }
    const totalsGridQuestionPath = <QuestionPath>getTotalsGridQuestionPath(navTabNumber);

    const mainGridData = this.getQuestionValue(this.model, ...mainGridQuestionPath);

    if (mainGridData && Array.isArray(mainGridData) && mainGridData.length > 0) {
      // Calculate totals by summing all rows
      const totalsRow = this.calculateTotalsFromGridData(mainGridData, navTabNumber);

      if (totalsRow) {
        this.setTotalsGridData(...totalsGridQuestionPath, [totalsRow]);
      }
      else {
        this.setTotalsGridData(...totalsGridQuestionPath, []);
      }
    }
    else {
      this.setTotalsGridData(...totalsGridQuestionPath, []);
    }
  }

  private calculateTotalsFromGridData(gridData: any[], navTabNumber: number): any {
    if (!gridData || gridData.length === 0) {
      return null;
    }

    let totalsRow: any = {};

    switch (navTabNumber) {
      case NAV_TAB_NUMBERS.reportList.businessProgress:
        totalsRow = {
          area: '',
          surveyForm: 'TOTAL',
          submitComplete: gridData.reduce((sum, row) => sum + (row.submitComplete || 0), 0),
          submitPartial: gridData.reduce((sum, row) => sum + (row.submitPartial || 0), 0),
          acceptedComplete: gridData.reduce((sum, row) => sum + (row.acceptedComplete || 0), 0),
          acceptedPartial: gridData.reduce((sum, row) => sum + (row.acceptedPartial || 0), 0),
          rejectedComplete: gridData.reduce((sum, row) => sum + (row.rejectedComplete || 0), 0),
          rejectedPartial: gridData.reduce((sum, row) => sum + (row.rejectedPartial || 0), 0),
        };
        break;

      case NAV_TAB_NUMBERS.reportList.invoicingSummary:
        totalsRow = {
          inspector: '',
          formType: 'TOTAL',
          submitComplete: gridData.reduce((sum, row) => sum + (parseInt(row.submitComplete) || 0), 0),
          submitPartial: gridData.reduce((sum, row) => sum + (parseInt(row.submitPartial) || 0), 0),
          acceptedComplete: gridData.reduce((sum, row) => sum + (parseInt(row.acceptedComplete) || 0), 0),
          acceptedPartial: gridData.reduce((sum, row) => sum + (parseInt(row.acceptedPartial) || 0), 0),
          rejectedComplete: gridData.reduce((sum, row) => sum + (parseInt(row.rejectedComplete) || 0), 0),
          rejectedPartial: gridData.reduce((sum, row) => sum + (parseInt(row.rejectedPartial) || 0), 0),
        };
        break;
    }

    return totalsRow;
  }

  private setTotalsGridData(navTabNumber: number, pageNumber: number, sectionCode: string, questionCode: string, data: any[]): void {
    const totalGridClass = `totalGrid${navTabNumber}`;
    const sectionModel = this.responseParserService.findSectionModel(this.model, navTabNumber,pageNumber, null,sectionCode,null);

    if (sectionModel) {
      this.setQuestionValue(this.model, navTabNumber, pageNumber, sectionCode, questionCode, data);

      if (data && data.length > 0) {
        this.htmlHelperService.showTableByContainer(AppSelector, ComponentSelectors.reportListForm.reportList, totalGridClass);
        this.htmlHelperService.hideTableHeadersByContainer(AppSelector, ComponentSelectors.reportListForm.reportList, totalGridClass);
      }
      else {
        this.htmlHelperService.hideTableByContainer(AppSelector, ComponentSelectors.reportListForm.reportList, totalGridClass);
      }

    }
    else {
      console.warn(`Path not found: tabs.${navTabNumber}.${pageNumber}.${sectionCode}.${questionCode}`);
    }
  }

  actionButton_businessProgressPlaceholder_datagrid_custom_exportToCsv_Clicked(event: any) {
    const filterRequest = this.getFilterRequest(QUESTION_CODES.reportList.businessProgressFilter);

    this.appDataService.getCsv(
      API_ENDPOINTS.dashboard.exportReports,
      filterRequest,
      'Dashboard'
    );
  }
}
